// POS shift store — in-memory + localStorage persistence.
//
// A "shift" represents a cashier's working session on a specific event.
// Starting a shift requires:
//   1. Selecting an event (or starting a new one)
//   2. Identifying the cashier (the user opening the shift, or a
//      delegated cashier they pick from the user list)
//
// Shift state is persisted to localStorage so a page refresh keeps the
// cashier in the middle of their shift. The store also tracks per-shift
// aggregates (transactions, sales) for the "End shift" summary.

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface PosShiftState {
  /** Active event for this shift */
  eventId: string | null;
  eventName: string | null;
  eventCode: string | null;
  /** Cashier operating the shift */
  cashierId: string;
  cashierName: string;
  /** When the shift started (epoch ms) */
  startedAt: number;
  /** Per-shift transaction count + sales (client-side aggregation) */
  transactions: number;
  totalSales: number;
  /** Why the last shift was ended automatically, shown on the start screen. Not persisted. */
  endedNotice: string | null;
}

interface PosShiftStore extends PosShiftState {
  startShift: (input: {
    eventId: string;
    eventName: string;
    eventCode: string;
    cashierId: string;
    cashierName: string;
  }) => void;
  endShift: (notice?: string) => void;
  clearNotice: () => void;
  /** Increment after each successful transaction. */
  recordTransaction: (grandTotal: number) => void;
}

export const usePosShiftStore = create<PosShiftStore>()(
  persist(
    (set) => ({
      eventId: null,
      eventName: null,
      eventCode: null,
      cashierId: '',
      cashierName: '',
      startedAt: 0,
      transactions: 0,
      totalSales: 0,
      endedNotice: null,

      startShift: (input) =>
        set({
          eventId: input.eventId,
          eventName: input.eventName,
          eventCode: input.eventCode,
          cashierId: input.cashierId,
          cashierName: input.cashierName,
          startedAt: Date.now(),
          transactions: 0,
          totalSales: 0,
          endedNotice: null,
        }),

      clearNotice: () => set({ endedNotice: null }),

      endShift: (notice) =>
        set({
          endedNotice: notice ?? null,
          eventId: null,
          eventName: null,
          eventCode: null,
          cashierId: '',
          cashierName: '',
          startedAt: 0,
          transactions: 0,
          totalSales: 0,
        }),

      recordTransaction: (grandTotal) =>
        set((state) => ({
          transactions: state.transactions + 1,
          totalSales: state.totalSales + grandTotal,
        })),
    }),
    {
      name: 'ophth-pos:shift',
      partialize: (s) => ({
        eventId: s.eventId,
        eventName: s.eventName,
        eventCode: s.eventCode,
        cashierId: s.cashierId,
        cashierName: s.cashierName,
        startedAt: s.startedAt,
        // Don't persist counters — they'll be re-derived from the
        // server on the next render. We still keep them in memory for
        // the duration of the session.
        transactions: 0,
        totalSales: 0,
      }),
    },
  ),
);
